import {
  RESTAURANT_CUSTOMS_STATE_KEY,
  getPersistedAppState,
  setPersistedAppState,
} from '@/services/offline/appState';

import { readTabContext } from '@/services/auth/partnerSessionStorage';

export const DEFAULT_CUSTOMER_BACKGROUND_COLOR = '#F3F3F3';

let restaurantCustomsCache = {
  primary_color: null,
  secondary_color: null,
  customer_background_color: null,
};
let restaurantCustomsHydrated = false;
let restaurantCustomsHydrationPromise = null;

export function applyRestaurantCustoms() {
  const root = document.documentElement;

  const primary = restaurantCustomsCache.primary_color || '#F47700';
  const secondary = restaurantCustomsCache.secondary_color || null;

  root.style.setProperty('--primary-color', primary);
  root.style.setProperty('--secondary-color', secondary);

  const rgbPrimary = hexToRgb(primary);
  root.style.setProperty('--primary-color-rgb', rgbPrimary);

  // Fondo de la parte de clientes (QR). El tono decide el color del texto que
  // va directamente sobre el fondo (cabecera, "Powered by"...).
  const customerBackground = normalizeHexColor(restaurantCustomsCache.customer_background_color)
    || DEFAULT_CUSTOMER_BACKGROUND_COLOR;
  const customerTone = isDarkColor(customerBackground) ? 'dark' : 'light';
  root.style.setProperty('--customer-bg-color', customerBackground);
  root.style.setProperty('--customer-bg-text', customerTone === 'dark' ? '#FFFFFF' : '#0A2533');
  root.dataset.customerBgTone = customerTone;
}

export function normalizeHexColor(value) {
  const color = String(value || '').trim();
  return /^#[0-9a-f]{6}$/i.test(color) ? color.toUpperCase() : null;
}

// Luminancia relativa (WCAG). Por debajo de ~0.18 el texto blanco contrasta más que el oscuro.
export function isDarkColor(hex) {
  const color = normalizeHexColor(hex);
  if (!color) return false;
  const [r, g, b] = [1, 3, 5].map((index) => {
    const channel = parseInt(color.slice(index, index + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.179;
}

function readLegacyRestaurantColor(key) {
  try {
    return readTabContext(key);
  } catch (error) {
    console.warn('Unable to read legacy restaurant color:', error);
    return null;
  }
}

async function loadRestaurantCustomsFromStorage() {
  const persistedCustoms = await getPersistedAppState(RESTAURANT_CUSTOMS_STATE_KEY);
  if (persistedCustoms) {
    return {
      primary_color: persistedCustoms.primary_color || null,
      secondary_color: persistedCustoms.secondary_color || null,
      customer_background_color: persistedCustoms.customer_background_color || null,
    };
  }

  const legacyCustoms = {
    primary_color: readLegacyRestaurantColor('restaurant_primary_color'),
    secondary_color: readLegacyRestaurantColor('restaurant_secondary_color'),
    customer_background_color: null,
  };

  if (legacyCustoms.primary_color || legacyCustoms.secondary_color) {
    await setPersistedAppState(RESTAURANT_CUSTOMS_STATE_KEY, legacyCustoms);
  }

  return legacyCustoms;
}

export async function hydrateRestaurantCustoms() {
  if (restaurantCustomsHydrated) {
    return restaurantCustomsCache;
  }

  if (!restaurantCustomsHydrationPromise) {
    restaurantCustomsHydrationPromise = (async () => {
      restaurantCustomsCache = await loadRestaurantCustomsFromStorage();
      restaurantCustomsHydrated = true;
      return restaurantCustomsCache;
    })();
  }

  try {
    return await restaurantCustomsHydrationPromise;
  } finally {
    restaurantCustomsHydrationPromise = null;
  }
}

export async function persistRestaurantCustoms(customs = {}) {
  restaurantCustomsCache = {
    primary_color: customs.primary_color || restaurantCustomsCache.primary_color || null,
    secondary_color: customs.secondary_color || restaurantCustomsCache.secondary_color || null,
    // A diferencia de los colores de marca, el fondo puede volver a "por defecto":
    // si llega la clave (aunque sea null) manda lo que diga el restaurante.
    customer_background_color: Object.prototype.hasOwnProperty.call(customs, 'customer_background_color')
      ? normalizeHexColor(customs.customer_background_color)
      : restaurantCustomsCache.customer_background_color || null,
  };
  restaurantCustomsHydrated = true;

  await setPersistedAppState(RESTAURANT_CUSTOMS_STATE_KEY, restaurantCustomsCache);
  return restaurantCustomsCache;
}

export async function initAndApplyRestaurantCustoms(token = null, restaurant_id = null) {
  try {
    let customization = null;
    if (restaurant_id === null) {
      const response = await fetch(`${process.env.VUE_APP_API_URL}/v1/api/app/me_table`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      customization = data.restaurant?.customization;
    } else {
      const response = await fetch(`${process.env.VUE_APP_API_URL}/v1/api/app/restaurant_customization?restaurant_id=${restaurant_id}`, {});
      customization = await response.json();
    }

    if (customization) {
      await persistRestaurantCustoms({
        primary_color: customization.primary_color || restaurantCustomsCache.primary_color,
        secondary_color: customization.secondary_color || restaurantCustomsCache.secondary_color,
        customer_background_color: customization.customer_background_color || null,
      });
    }
  } catch (e) {
    console.warn('Error al cargar personalización del restaurante:', e);
  }

  applyRestaurantCustoms();
}

function hexToRgb(hex) {
  const cleanHex = hex?.replace('#', '') || 'f47700';
  const bigint = parseInt(cleanHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r}, ${g}, ${b}`;
}
